import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Badge } from './ui/badge';
import { Bookmark, BookmarkCheck, Clock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

import { API, authFetch } from '../lib/api';

export const ArticleCard = ({ article, onBookmarkChange }) => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(String(dateString).replace(' ', 'T'));
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
    });
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const handleBookmark = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user) {
      toast.error('Please sign in to bookmark articles');
      return;
    }

    try {
      const method = article.is_bookmarked ? 'DELETE' : 'POST';
      const response = await authFetch(`${API}/articles/${article.article_id}/bookmark`, {
        method,
      });

      if (response.ok) {
        toast.success(article.is_bookmarked ? 'Removed from bookmarks' : 'Added to bookmarks');
        if (onBookmarkChange) {
          onBookmarkChange(article.article_id, !article.is_bookmarked);
        }
      }
    } catch (error) {
      toast.error('Failed to update bookmark');
    }
  };

  const getExcerpt = (content) => {
    if (!content || !content.content) return '';
    
    let text = '';
    const extractText = (nodes) => {
      for (const node of nodes) {
        if (node.type === 'text') {
          text += node.text + ' ';
        } else if (node.content) {
          extractText(node.content);
        }
        if (text.length > 150) break;
      }
    };
    
    extractText(content.content || []);
    return text.trim().slice(0, 150) + (text.length > 150 ? '...' : '');
  };

  return (
    <article 
      className="group py-6 cursor-pointer hover-lift"
      onClick={() => navigate(`/article/${article.article_id}`)}
      data-testid={`article-card-${article.article_id}`}
    >
      <div className="flex gap-6">
        <div className="flex-1 min-w-0">
          {/* Author Info */}
          <div className="flex items-center gap-2 mb-3">
            <Avatar className="w-6 h-6 ring-1 ring-border transition-all group-hover:ring-accent/40">
              <AvatarImage src={article.author_picture} alt={article.author_name} />
              <AvatarFallback className="text-xs bg-muted">
                {getInitials(article.author_name)}
              </AvatarFallback>
            </Avatar>
            <Link 
              to={`/profile/${article.author_id}`}
              onClick={(e) => e.stopPropagation()}
              className="text-sm font-medium hover:text-accent transition-colors"
              data-testid={`author-link-${article.article_id}`}
            >
              {article.author_name}
            </Link>
          </div>

          {/* Title & Subtitle */}
          <h2 
            className="text-xl font-bold mb-2 group-hover:text-accent transition-colors duration-200 line-clamp-2 flex items-start gap-2"
            style={{ fontFamily: 'Fraunces, serif' }}
          >
            <span className="flex-1">{article.title}</span>
            {article.is_published === false && (
              <Badge
                variant="outline"
                className="shrink-0 text-[10px] font-medium uppercase tracking-wider border-amber-500/40 text-amber-600 dark:text-amber-300 bg-amber-500/10"
                data-testid={`hidden-badge-${article.article_id}`}
              >
                Hidden
              </Badge>
            )}
          </h2>
          
          {article.subtitle ? (
            <p className="text-muted-foreground mb-3 line-clamp-2 text-sm sm:text-base">
              {article.subtitle}
            </p>
          ) : (
            <p className="text-muted-foreground mb-3 line-clamp-2 text-sm sm:text-base">
              {getExcerpt(article.content)}
            </p>
          )}

          {/* Meta & Actions */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-muted-foreground flex-wrap">
              <span>{formatDate(article.published_at || article.created_at)}</span>
              <span className="flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                {article.reading_time} min
              </span>
              {article.tags?.length > 0 && (
                <Badge 
                  variant="secondary" 
                  className="rounded-full text-xs hidden sm:inline-flex"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/topic/${article.tags[0]}`);
                  }}
                >
                  {article.tags[0]}
                </Badge>
              )}
            </div>

            <button
              onClick={handleBookmark}
              className="p-1.5 hover:bg-muted rounded-full transition-all duration-200 press-scale"
              data-testid={`bookmark-btn-${article.article_id}`}
            >
              {article.is_bookmarked ? (
                <BookmarkCheck className="w-4 h-4 sm:w-5 sm:h-5 text-accent" />
              ) : (
                <Bookmark className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground group-hover:text-foreground transition-colors" />
              )}
            </button>
          </div>
        </div>

        {/* Cover Image */}
        {article.cover_image && (
          <div className="hidden sm:block w-28 h-28 md:w-32 md:h-32 flex-shrink-0 overflow-hidden rounded-lg">
            <img
              src={article.cover_image}
              alt=""
              className="w-full h-full object-cover img-zoom"
            />
          </div>
        )}
      </div>
    </article>
  );
};

export default ArticleCard;
